import { ColumnDef } from '@tanstack/react-table';
import { PencilSimple, Trash } from '@phosphor-icons/react';
import { DataTableColumnHeader, StatusChip } from '@/components/core';
import { Button } from '@/components/ui-kit/button';
import { formatDateOnly } from '@/lib/utils/custom-date/custom-date';
import { TimeSheetRecord } from '../../types/timesheet.types';

const formatHours = (hours: number) => `${hours.toFixed(2)}h`;

const totalBillable = (record: TimeSheetRecord) =>
  record.Tasks.reduce((sum, task) => sum + task.Billable, 0);
const totalNonBillable = (record: TimeSheetRecord) =>
  record.Tasks.reduce((sum, task) => sum + task.NonBillable, 0);

interface CreateTimesheetTableColumnsProps {
  t: (key: string) => string;
  onEdit: (record: TimeSheetRecord) => void;
  onDelete: (record: TimeSheetRecord) => void;
}

export const createTimesheetTableColumns = ({
  t,
  onEdit,
  onDelete,
}: CreateTimesheetTableColumnsProps): ColumnDef<TimeSheetRecord>[] => [
  {
    id: 'TaskDate',
    meta: { filter: { type: 'date' } },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('DATE')} />,
    accessorFn: (row) => row.TaskDate,
    cell: ({ row }) => <span>{formatDateOnly(row.original.TaskDate)}</span>,
  },
  {
    id: 'TaskCount',
    // Counted from the tasks JSON after the page loads; not a field the server can sort on.
    enableSorting: false,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={t('MY_TIMESHEET_TASKS')} />
    ),
    accessorFn: (row) => row.Tasks.length,
  },
  {
    id: 'TotalBillable',
    enableSorting: false,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={t('MY_TIMESHEET_BILLABLE_HOURS')} />
    ),
    accessorFn: (row) => totalBillable(row),
    cell: ({ row }) => <span>{formatHours(totalBillable(row.original))}</span>,
  },
  {
    id: 'TotalNonBillable',
    enableSorting: false,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={t('MY_TIMESHEET_NON_BILLABLE_HOURS')} />
    ),
    accessorFn: (row) => totalNonBillable(row),
    cell: ({ row }) => <span>{formatHours(totalNonBillable(row.original))}</span>,
  },
  {
    id: 'IsUnderTime',
    // Card: the status chip top right.
    meta: {
      mobile: 'badge',
      filter: {
        type: 'options',
        options: [
          { value: true, label: t('MY_TIMESHEET_UNDER_TIME') },
          { value: false, label: t('MY_TIMESHEET_ON_TARGET') },
        ],
      },
    },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('STATUS')} />,
    accessorFn: (row) => row.IsUnderTime,
    cell: ({ row }) =>
      row.original.IsUnderTime ? (
        <StatusChip tone="error">{t('MY_TIMESHEET_UNDER_TIME')}</StatusChip>
      ) : (
        <StatusChip tone="success">{t('MY_TIMESHEET_ON_TARGET')}</StatusChip>
      ),
  },
  {
    id: 'actions',
    header: () => <span className="text-xs font-medium">{t('ACTION')}</span>,
    enableSorting: false,
    enableHiding: false,
    cell: ({ row }) => (
      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onEdit(row.original)}
          aria-label={t('EDIT')}
        >
          <PencilSimple className="size-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onDelete(row.original)}
          aria-label={t('DELETE')}
        >
          <Trash className="size-4" />
        </Button>
      </div>
    ),
  },
];
